import React, { useState, useEffect, useRef } from 'react';
import { 
  CreditCard, Calendar, Clock, AlertTriangle, CheckCircle, 
  FileText, DollarSign, AlertCircle, Ban, Download, Eye, Printer, X,
  Globe, Wallet, Building
} from 'lucide-react';
import axios from 'axios';
import { formatDate } from '../../utils/dateTimeUtils';

const MySubscription = () => {
  const [subscription, setSubscription] = useState(null);
  const [invoices, setInvoices] = useState({ pending_invoices: [], paid_invoices: [], total_pending: 0 });
  const [loading, setLoading] = useState(true);
  const [showPayModal, setShowPayModal] = useState(false);
  const [showInvoiceModal, setShowInvoiceModal] = useState(false);
  const [selectedInvoice, setSelectedInvoice] = useState(null);
  const [paymentTab, setPaymentTab] = useState('online'); // 'online' or 'manual'
  const [availableGateways, setAvailableGateways] = useState({ gateways: [], manual_payment_enabled: true });
  const [processingPayment, setProcessingPayment] = useState(false);
  const [paymentForm, setPaymentForm] = useState({
    payment_method: 'bank_transfer',
    transaction_id: '',
    notes: ''
  });
  const invoicePrintRef = useRef(null);

  const API_URL = process.env.REACT_APP_BACKEND_URL;

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      setLoading(true);
      const token = localStorage.getItem('token');
      const headers = { Authorization: `Bearer ${token}` };

      const [subRes, invRes, gatewayRes] = await Promise.all([
        axios.get(`${API_URL}/api/subscriptions/my-subscription`, { headers }),
        axios.get(`${API_URL}/api/subscriptions/my-invoices`, { headers }),
        axios.get(`${API_URL}/api/payment-gateway/available`, { headers }).catch(() => ({ data: { gateways: [], manual_payment_enabled: true } }))
      ]);

      setSubscription(subRes.data);
      
      // Handle both old and new invoice response formats
      const invoiceData = invRes.data;
      if (invoiceData.invoices && !invoiceData.pending_invoices) {
        // New format - split invoices into pending and paid
        const allInvoices = invoiceData.invoices || [];
        const pendingInvoices = allInvoices.filter(inv => inv.status === 'pending' || inv.status === 'overdue');
        const paidInvoices = allInvoices.filter(inv => inv.status === 'paid');
        setInvoices({
          pending_invoices: pendingInvoices,
          paid_invoices: paidInvoices,
          total_pending: pendingInvoices.reduce((sum, inv) => sum + (inv.amount || 0), 0),
          ...invoiceData.summary
        });
      } else {
        // Old format - use as is
        setInvoices(invoiceData);
      }
      
      setAvailableGateways(gatewayRes.data);
      
      // Set default payment tab based on available options
      if (gatewayRes.data.gateways?.length > 0) {
        setPaymentTab('online');
      } else {
        setPaymentTab('manual');
      }
    } catch (error) {
      console.error('Error fetching data:', error);
    } finally {
      setLoading(false);
    }
  };

  const handlePayInvoice = async (e) => {
    e.preventDefault();
    try {
      const token = localStorage.getItem('token');
      const res = await axios.post(
        `${API_URL}/api/subscriptions/pay-invoice`,
        {
          invoice_id: selectedInvoice.id,
          ...paymentForm
        },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      
      alert(`Payment successful! Your subscription is now valid until ${res.data.new_end_date}`);
      setShowPayModal(false);
      setPaymentForm({ payment_method: 'bank_transfer', transaction_id: '', notes: '' });
      fetchData();
    } catch (error) {
      alert(error.response?.data?.detail || 'Payment failed. Please try again.');
    }
  };

  const handleOnlinePayment = async (gateway) => {
    if (!selectedInvoice) return;
    
    setProcessingPayment(true);
    try {
      const token = localStorage.getItem('token');
      
      // Create payment order
      const orderRes = await axios.post(
        `${API_URL}/api/payment-gateway/create-order`,
        {
          invoice_id: selectedInvoice.id,
          gateway: gateway.name
        },
        { headers: { Authorization: `Bearer ${token}` } }
      );

      const orderData = orderRes.data;

      if (gateway.name === 'razorpay' && orderData.gateway_order_id) {
        // Load Razorpay script if not loaded
        if (!window.Razorpay) {
          const script = document.createElement('script');
          script.src = 'https://checkout.razorpay.com/v1/checkout.js';
          script.async = true;
          document.body.appendChild(script);
          await new Promise(resolve => script.onload = resolve);
        }

        const options = {
          key: orderData.key_id,
          amount: orderData.amount,
          currency: orderData.currency,
          name: 'ERP Subscription',
          description: `Invoice: ${selectedInvoice.invoice_number}`,
          order_id: orderData.gateway_order_id,
          handler: async function (response) {
            // Verify payment
            try {
              const verifyRes = await axios.post(
                `${API_URL}/api/payment-gateway/verify-payment`,
                {
                  gateway: 'razorpay',
                  order_id: orderData.order_id,
                  razorpay_payment_id: response.razorpay_payment_id,
                  razorpay_order_id: response.razorpay_order_id,
                  razorpay_signature: response.razorpay_signature
                },
                { headers: { Authorization: `Bearer ${token}` } }
              );
              
              alert('Payment successful! Your subscription has been extended.');
              setShowPayModal(false);
              fetchData();
            } catch (err) {
              alert('Payment verification failed. Please contact support.');
            }
          },
          prefill: {
            email: subscription?.tenant_name || ''
          },
          theme: {
            color: '#2563eb'
          },
          modal: {
            ondismiss: function() {
              setProcessingPayment(false);
            }
          }
        };

        const rzp = new window.Razorpay(options);
        rzp.open();
      } else {
        // For Stripe/PayU - show message
        alert(orderData.message || 'Gateway integration in progress. Please use manual payment.');
      }
    } catch (error) {
      alert(error.response?.data?.detail || 'Failed to initiate payment. Please try manual payment.');
    } finally {
      setProcessingPayment(false);
    }
  };

  const handleViewInvoice = (invoice) => {
    setSelectedInvoice(invoice);
    setShowInvoiceModal(true);
  };

  const handlePrintInvoice = () => {
    const printContent = invoicePrintRef.current;
    const printWindow = window.open('', '_blank');
    printWindow.document.write(`
      <html>
        <head>
          <title>Invoice - ${selectedInvoice?.invoice_number}</title>
          <style>
            body { font-family: Arial, sans-serif; padding: 20px; }
            .invoice-header { text-align: center; margin-bottom: 30px; border-bottom: 2px solid #333; padding-bottom: 20px; }
            .invoice-header h1 { color: #2563eb; margin: 0; font-size: 28px; }
            .invoice-header p { color: #666; margin: 5px 0; }
            .invoice-details { display: grid; grid-template-columns: 1fr 1fr; gap: 30px; margin-bottom: 30px; }
            .invoice-details h3 { color: #333; margin-bottom: 10px; font-size: 14px; text-transform: uppercase; }
            .invoice-details p { margin: 3px 0; color: #555; font-size: 13px; }
            .bill-to { background: #f9fafb; padding: 15px; border-radius: 8px; margin-bottom: 20px; }
            .bill-to h3 { color: #333; margin-bottom: 10px; font-size: 14px; text-transform: uppercase; }
            .bill-to p { margin: 3px 0; color: #555; }
            .invoice-table { width: 100%; border-collapse: collapse; margin-bottom: 30px; }
            .invoice-table th, .invoice-table td { border: 1px solid #ddd; padding: 12px; text-align: left; }
            .invoice-table th { background: #f5f5f5; font-weight: bold; }
            .total-row { font-weight: bold; background: #f0f9ff; }
            .status-badge { display: inline-block; padding: 4px 12px; border-radius: 20px; font-size: 12px; font-weight: bold; }
            .status-paid { background: #dcfce7; color: #166534; }
            .status-pending { background: #fef3c7; color: #92400e; }
            .footer { text-align: center; margin-top: 40px; padding-top: 20px; border-top: 1px solid #ddd; color: #666; font-size: 11px; }
            .footer p { margin: 3px 0; }
            @media print { body { padding: 0; } }
          </style>
        </head>
        <body>
          ${printContent.innerHTML}
        </body>
      </html>
    `);
    printWindow.document.close();
    printWindow.print();
  };

  const getStatusColor = (status, isBlocked) => {
    if (isBlocked) return 'text-red-600';
    const colors = {
      active: 'text-green-600',
      grace_period: 'text-yellow-600',
      expired: 'text-orange-600',
      blocked: 'text-red-600'
    };
    return colors[status] || 'text-gray-600';
  };

  const getStatusIcon = (status, isBlocked) => {
    if (isBlocked) return Ban;
    const icons = {
      active: CheckCircle,
      grace_period: Clock,
      expired: AlertTriangle,
      blocked: Ban
    };
    return icons[status] || AlertCircle;
  };

  // Bug fix: removed shadowing formatDate – using imported one

  const numberToWords = (num) => {
    const ones = ['', 'One', 'Two', 'Three', 'Four', 'Five', 'Six', 'Seven', 'Eight', 'Nine', 'Ten',
      'Eleven', 'Twelve', 'Thirteen', 'Fourteen', 'Fifteen', 'Sixteen', 'Seventeen', 'Eighteen', 'Nineteen'];
    const tens = ['', '', 'Twenty', 'Thirty', 'Forty', 'Fifty', 'Sixty', 'Seventy', 'Eighty', 'Ninety'];
    
    if (num === 0) return 'Zero';
    if (num < 20) return ones[num];
    if (num < 100) return tens[Math.floor(num / 10)] + (num % 10 ? ' ' + ones[num % 10] : '');
    if (num < 1000) return ones[Math.floor(num / 100)] + ' Hundred' + (num % 100 ? ' ' + numberToWords(num % 100) : '');
    if (num < 100000) return numberToWords(Math.floor(num / 1000)) + ' Thousand' + (num % 1000 ? ' ' + numberToWords(num % 1000) : '');
    if (num < 10000000) return numberToWords(Math.floor(num / 100000)) + ' Lakh' + (num % 100000 ? ' ' + numberToWords(num % 100000) : '');
    return numberToWords(Math.floor(num / 10000000)) + ' Crore' + (num % 10000000 ? ' ' + numberToWords(num % 10000000) : '');
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-screen">
        <div className="animate-spin w-8 h-8 border-4 border-blue-600 border-t-transparent rounded-full"></div>
      </div>
    );
  }

  const sub = subscription?.subscription || {};
  const StatusIcon = getStatusIcon(sub.status || subscription?.status, sub.is_blocked);
  
  // Use top-level data as fallback for subscription details
  const planName = subscription?.plan_name || sub.plan_name || sub.plan || 'N/A';
  const planType = sub.plan_type || subscription?.billing_cycle || 'yearly';
  const subStatus = sub.status || subscription?.status || 'active';
  const subEndDate = sub.end_date || subscription?.end_date || subscription?.subscription_end_date;
  const subAmount = sub.amount || 0;
  const hasPlan = planName !== 'N/A' || sub.plan_type || subscription?.plan_name;

  // Combine all invoices for history
  const allInvoices = [...(invoices.pending_invoices || []), ...(invoices.paid_invoices || [])];

  return (
    <div className="p-6 max-w-5xl mx-auto" data-testid="my-subscription-page">
      {/* Header */}
      <div className="mb-6">
        <h1 className="text-3xl font-bold text-gray-900">Software Subscription</h1>
        <p className="text-gray-600 mt-1">Manage your ERP software subscription</p>
      </div>

      {/* Warning Banner if Blocked or Expired */}
      {(sub.is_blocked || subscription?.is_expired) && (
        <div className="bg-red-50 border border-red-200 rounded-lg p-4 mb-6 flex items-start gap-3">
          <AlertTriangle className="h-6 w-6 text-red-600 flex-shrink-0 mt-0.5" />
          <div>
            <h3 className="font-bold text-red-800">
              {sub.is_blocked ? 'Your Account is Blocked' : 'Your Subscription has Expired'}
            </h3>
            <p className="text-red-700 text-sm mt-1">
              {sub.is_blocked 
                ? 'Your access has been restricted due to non-payment. Please clear pending invoices to restore access.'
                : 'Your subscription period has ended. Please pay the pending invoice to continue using the system.'}
            </p>
          </div>
        </div>
      )}

      {/* Warning Banner if Expiring Soon */}
      {subscription?.is_expiring_soon && !sub.is_blocked && (
        <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4 mb-6 flex items-start gap-3">
          <Clock className="h-6 w-6 text-yellow-600 flex-shrink-0 mt-0.5" />
          <div>
            <h3 className="font-bold text-yellow-800">Subscription Expiring Soon</h3>
            <p className="text-yellow-700 text-sm mt-1">
              Your subscription will expire in <strong>{subscription.days_remaining} days</strong>. 
              Please pay the pending invoice to avoid service interruption.
            </p>
          </div>
        </div>
      )}

      {/* Current Subscription Card */}
      <div className="bg-white rounded-xl shadow-lg mb-6 overflow-hidden">
        <div className="bg-gradient-to-r from-blue-600 to-indigo-600 text-white p-6">
          <h2 className="text-xl font-bold">Current Subscription</h2>
          <p className="text-blue-100 mt-1">{subscription?.tenant_name}</p>
        </div>
        
        <div className="p-6">
          {hasPlan ? (
            <>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
              <div>
                <p className="text-sm text-gray-500 mb-1">Plan</p>
                <p className="text-xl font-bold capitalize">{planName}</p>
              </div>
              
              <div>
                <p className="text-sm text-gray-500 mb-1">Status</p>
                <div className={`flex items-center gap-2 ${getStatusColor(subStatus, sub.is_blocked)}`}>
                  <StatusIcon className="h-5 w-5" />
                  <span className="text-xl font-bold capitalize">
                    {sub.is_blocked ? 'Blocked' : subStatus?.replace('_', ' ')}
                  </span>
                </div>
              </div>
              
              <div>
                <p className="text-sm text-gray-500 mb-1">Valid Until</p>
                <p className="text-xl font-bold">{subEndDate ? formatDate(subEndDate) : 'No Expiry'}</p>
                {subscription?.days_remaining !== undefined && subscription.days_remaining !== -1 && (
                  <p className={`text-sm ${subscription.days_remaining > 7 ? 'text-green-600' : subscription.days_remaining > 0 ? 'text-orange-600' : 'text-red-600'}`}>
                    {subscription.days_remaining > 0 
                      ? `${subscription.days_remaining} days remaining`
                      : `${Math.abs(subscription.days_remaining)} days overdue`}
                  </p>
                )}
                {subscription?.days_remaining === -1 && (
                  <p className="text-sm text-green-600">Unlimited Access</p>
                )}
              </div>
              
              <div>
                <p className="text-sm text-gray-500 mb-1">Subscription Amount</p>
                <p className="text-xl font-bold">₹{(subscription?.next_renewal_price || subAmount || 0)?.toLocaleString('en-IN')}</p>
                <p className="text-sm text-gray-500">per year</p>
              </div>
            </div>
            
            {/* Renewal Price Info */}
            {subscription?.next_renewal_price > 0 && (
              <div className="mt-6 pt-4 border-t border-gray-100">
                <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                  <div className="bg-blue-50 rounded-lg p-4">
                    <p className="text-xs text-blue-600 font-medium uppercase tracking-wider mb-1">Next Renewal Price</p>
                    <p className="text-2xl font-bold text-blue-700">₹{subscription.next_renewal_price?.toLocaleString('en-IN')}</p>
                    <p className="text-xs text-blue-500 mt-1">per year</p>
                  </div>
                  {subscription?.custom_renewal_price && (
                    <div className="bg-green-50 rounded-lg p-4">
                      <p className="text-xs text-green-600 font-medium uppercase tracking-wider mb-1">Custom Price Applied</p>
                      <p className="text-2xl font-bold text-green-700">₹{subscription.custom_renewal_price?.toLocaleString('en-IN')}</p>
                      <p className="text-xs text-green-500 mt-1">special rate for your institution</p>
                    </div>
                  )}
                  {subscription?.plan_first_time_price > 0 && (
                    <div className="bg-gray-50 rounded-lg p-4">
                      <p className="text-xs text-gray-500 font-medium uppercase tracking-wider mb-1">Plan Base Price</p>
                      <p className="text-2xl font-bold text-gray-600">₹{subscription.plan_first_time_price?.toLocaleString('en-IN')}</p>
                      <p className="text-xs text-gray-400 mt-1">first time / year</p>
                    </div>
                  )}
                </div>
              </div>
            )}
            </>
          ) : (
            <div className="text-center py-8">
              <CreditCard className="h-12 w-12 text-gray-300 mx-auto mb-3" />
              <p className="text-gray-500">No subscription configured yet</p>
              <p className="text-sm text-gray-400 mt-1">Please contact support to setup your subscription</p>
            </div>
          )}
        </div>
      </div>

      {/* Pending Invoices */}
      {invoices.pending_invoices.length > 0 && (
        <div className="bg-white rounded-xl shadow-lg mb-6">
          <div className="px-6 py-4 border-b flex items-center justify-between">
            <h2 className="text-xl font-bold text-gray-900 flex items-center gap-2">
              <AlertCircle className="h-5 w-5 text-orange-500" />
              Pending Invoices
            </h2>
            <span className="text-lg font-bold text-orange-600">
              Total: ₹{invoices.total_pending.toLocaleString('en-IN')}
            </span>
          </div>
          
          <div className="divide-y">
            {invoices.pending_invoices.map((invoice) => (
              <div key={invoice.id} className="p-6 flex flex-wrap items-center justify-between gap-4">
                <div className="flex items-start gap-4">
                  <div className="p-3 bg-orange-100 rounded-lg">
                    <FileText className="h-6 w-6 text-orange-600" />
                  </div>
                  <div>
                    <p className="font-bold text-gray-900">{invoice.invoice_number}</p>
                    <p className="text-sm text-gray-500">
                      {invoice.billing_period_start} to {invoice.billing_period_end}
                    </p>
                    <p className="text-sm text-gray-500">Due: {invoice.due_date}</p>
                  </div>
                </div>
                
                <div className="flex items-center gap-4">
                  <div className="text-right">
                    <p className="text-2xl font-bold text-gray-900">₹{invoice.amount?.toLocaleString('en-IN')}</p>
                    <span className="px-2 py-1 bg-yellow-100 text-yellow-800 rounded text-xs font-medium">
                      PENDING
                    </span>
                  </div>
                  <div className="flex gap-2">
                    <button
                      onClick={() => handleViewInvoice(invoice)}
                      className="p-2 bg-blue-100 text-blue-600 rounded-lg hover:bg-blue-200"
                      title="View Invoice"
                      data-testid="view-invoice-btn"
                    >
                      <Eye className="h-5 w-5" />
                    </button>
                    <button
                      onClick={() => {
                        setSelectedInvoice(invoice);
                        setShowPayModal(true);
                      }}
                      className="px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 font-medium flex items-center gap-2"
                      data-testid="pay-invoice-btn"
                    >
                      <DollarSign className="h-5 w-5" />
                      Pay Now
                    </button>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Payment History / All Invoices */}
      <div className="bg-white rounded-xl shadow-lg">
        <div className="px-6 py-4 border-b">
          <h2 className="text-xl font-bold text-gray-900">Invoice History</h2>
        </div>
        
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead className="bg-gray-50 border-b">
              <tr>
                <th className="text-left p-4 font-semibold text-gray-700">Invoice #</th>
                <th className="text-left p-4 font-semibold text-gray-700">Period</th>
                <th className="text-left p-4 font-semibold text-gray-700">Amount</th>
                <th className="text-left p-4 font-semibold text-gray-700">Due Date</th>
                <th className="text-left p-4 font-semibold text-gray-700">Status</th>
                <th className="text-left p-4 font-semibold text-gray-700">Payment Date</th>
                <th className="text-center p-4 font-semibold text-gray-700">Actions</th>
              </tr>
            </thead>
            <tbody className="divide-y">
              {allInvoices.length === 0 ? (
                <tr>
                  <td colSpan="7" className="text-center py-8 text-gray-500">
                    No invoices found
                  </td>
                </tr>
              ) : (
                allInvoices.map((invoice) => (
                  <tr key={invoice.id} className="hover:bg-gray-50">
                    <td className="p-4 font-mono text-sm">{invoice.invoice_number}</td>
                    <td className="p-4 text-sm">
                      {invoice.billing_period_start} to {invoice.billing_period_end}
                    </td>
                    <td className="p-4 font-medium">₹{invoice.amount?.toLocaleString('en-IN')}</td>
                    <td className="p-4 text-sm">{invoice.due_date}</td>
                    <td className="p-4">
                      <span className={`flex items-center gap-1 px-2 py-1 rounded text-xs font-medium w-fit ${
                        invoice.status === 'paid' ? 'bg-green-100 text-green-800' : 'bg-yellow-100 text-yellow-800'
                      }`}>
                        {invoice.status === 'paid' ? <CheckCircle className="h-3.5 w-3.5" /> : <Clock className="h-3.5 w-3.5" />}
                        {invoice.status?.toUpperCase()}
                      </span>
                    </td>
                    <td className="p-4 text-sm">
                      {invoice.payment_date ? formatDate(invoice.payment_date) : '-'}
                    </td>
                    <td className="p-4">
                      <div className="flex justify-center gap-2">
                        <button
                          onClick={() => handleViewInvoice(invoice)}
                          className="p-1.5 bg-blue-100 text-blue-600 rounded hover:bg-blue-200"
                          title="View Invoice"
                        >
                          <Eye className="h-4 w-4" />
                        </button>
                        <button
                          onClick={() => {
                            setSelectedInvoice(invoice);
                            setShowInvoiceModal(true);
                            setTimeout(() => handlePrintInvoice(), 100);
                          }}
                          className="p-1.5 bg-gray-100 text-gray-600 rounded hover:bg-gray-200"
                          title="Print Invoice"
                        >
                          <Printer className="h-4 w-4" />
                        </button>
                      </div>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      </div>

      {/* Pay Invoice Modal */}
      {showPayModal && selectedInvoice && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-xl w-full max-w-lg overflow-hidden">
            {/* Header */}
            <div className="bg-gradient-to-r from-blue-600 to-indigo-600 text-white p-4">
              <div className="flex items-center justify-between">
                <div>
                  <h2 className="text-xl font-bold">Pay Invoice</h2>
                  <p className="text-blue-100 text-sm mt-1">
                    {selectedInvoice.invoice_number} • ₹{selectedInvoice.amount?.toLocaleString('en-IN')}
                  </p>
                </div>
                <button onClick={() => setShowPayModal(false)} className="p-1 hover:bg-white/20 rounded">
                  <X className="h-5 w-5" />
                </button>
              </div>
            </div>

            {/* Tabs */}
            <div className="flex border-b">
              {availableGateways.gateways?.length > 0 && (
                <button
                  onClick={() => setPaymentTab('online')}
                  className={`flex-1 py-3 px-4 text-sm font-medium flex items-center justify-center gap-2 ${
                    paymentTab === 'online' ? 'border-b-2 border-blue-600 text-blue-600 bg-blue-50' : 'text-gray-500'
                  }`}
                >
                  <Globe className="h-4 w-4" />
                  Online Payment
                </button>
              )}
              {availableGateways.manual_payment_enabled && (
                <button
                  onClick={() => setPaymentTab('manual')}
                  className={`flex-1 py-3 px-4 text-sm font-medium flex items-center justify-center gap-2 ${
                    paymentTab === 'manual' ? 'border-b-2 border-blue-600 text-blue-600 bg-blue-50' : 'text-gray-500'
                  }`}
                >
                  <Building className="h-4 w-4" />
                  Bank Transfer / UPI
                </button>
              )}
            </div>

            <div className="p-6">
              {/* Online Payment Tab */}
              {paymentTab === 'online' && availableGateways.gateways?.length > 0 && (
                <div className="space-y-4">
                  <p className="text-sm text-gray-600 mb-4">
                    Select a payment gateway to pay securely online:
                  </p>
                  
                  {availableGateways.gateways.map((gateway) => (
                    <button
                      key={gateway.name}
                      onClick={() => handleOnlinePayment(gateway)}
                      disabled={processingPayment}
                      className="w-full p-4 border-2 rounded-xl hover:border-blue-500 hover:bg-blue-50 transition-all flex items-center justify-between group disabled:opacity-50"
                    >
                      <div className="flex items-center gap-3">
                        <div className={`p-2 rounded-lg ${
                          gateway.name === 'razorpay' ? 'bg-blue-100' :
                          gateway.name === 'stripe' ? 'bg-purple-100' : 'bg-green-100'
                        }`}>
                          <CreditCard className={`h-6 w-6 ${
                            gateway.name === 'razorpay' ? 'text-blue-600' :
                            gateway.name === 'stripe' ? 'text-purple-600' : 'text-green-600'
                          }`} />
                        </div>
                        <div className="text-left">
                          <p className="font-semibold text-gray-900">{gateway.display_name}</p>
                          <p className="text-xs text-gray-500">
                            {gateway.name === 'razorpay' ? 'UPI, Cards, Netbanking, Wallets' :
                             gateway.name === 'stripe' ? 'Cards, International Payments' : 
                             'UPI, Cards, Netbanking'}
                          </p>
                        </div>
                      </div>
                      <div className="flex items-center gap-2">
                        {gateway.is_test_mode && (
                          <span className="px-2 py-1 bg-yellow-100 text-yellow-700 text-xs rounded font-medium">
                            TEST
                          </span>
                        )}
                        <span className="text-blue-600 group-hover:translate-x-1 transition-transform">→</span>
                      </div>
                    </button>
                  ))}

                  {processingPayment && (
                    <div className="text-center py-4">
                      <div className="animate-spin w-6 h-6 border-2 border-blue-600 border-t-transparent rounded-full mx-auto"></div>
                      <p className="text-sm text-gray-500 mt-2">Processing payment...</p>
                    </div>
                  )}
                </div>
              )}

              {/* Manual Payment Tab */}
              {paymentTab === 'manual' && availableGateways.manual_payment_enabled && (
                <form onSubmit={handlePayInvoice}>
                  <p className="text-sm text-gray-600 mb-4">
                    Record your offline payment details:
                  </p>
                  
                  <div className="space-y-4">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">Payment Method *</label>
                      <select
                        value={paymentForm.payment_method}
                        onChange={(e) => setPaymentForm({ ...paymentForm, payment_method: e.target.value })}
                        className="w-full px-3 py-2 border rounded-lg"
                        required
                      >
                        <option value="bank_transfer">Bank Transfer / NEFT / RTGS</option>
                        <option value="upi">UPI</option>
                        <option value="cash">Cash</option>
                      </select>
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">Transaction ID / Reference *</label>
                      <input
                        type="text"
                        value={paymentForm.transaction_id}
                        onChange={(e) => setPaymentForm({ ...paymentForm, transaction_id: e.target.value })}
                        className="w-full px-3 py-2 border rounded-lg"
                        placeholder="Enter transaction reference number"
                        required
                      />
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">Notes</label>
                      <textarea
                        value={paymentForm.notes}
                        onChange={(e) => setPaymentForm({ ...paymentForm, notes: e.target.value })}
                        className="w-full px-3 py-2 border rounded-lg"
                        rows="2"
                        placeholder="Any additional notes..."
                      />
                    </div>
                  </div>

                  <div className="bg-blue-50 border border-blue-200 rounded-lg p-3 mt-4">
                    <p className="text-sm text-blue-800">
                      <strong>Note:</strong> Your payment will be verified and subscription will be extended upon confirmation.
                    </p>
                  </div>

                  <div className="flex gap-2 mt-6">
                    <button
                      type="submit"
                      className="flex-1 bg-green-600 text-white py-3 rounded-lg hover:bg-green-700 font-medium flex items-center justify-center gap-2"
                    >
                      <CheckCircle className="h-5 w-5" />
                      Submit Payment
                    </button>
                    <button
                      type="button"
                      onClick={() => setShowPayModal(false)}
                      className="flex-1 bg-gray-200 text-gray-700 py-3 rounded-lg hover:bg-gray-300 font-medium"
                    >
                      Cancel
                    </button>
                  </div>
                </form>
              )}

              {/* No payment options available */}
              {!availableGateways.gateways?.length && !availableGateways.manual_payment_enabled && (
                <div className="text-center py-8">
                  <AlertCircle className="h-12 w-12 text-gray-300 mx-auto mb-3" />
                  <p className="text-gray-500">No payment options available</p>
                  <p className="text-sm text-gray-400 mt-1">Please contact support</p>
                </div>
              )}
            </div>
          </div>
        </div>
      )}

      {/* View Invoice Modal */}
      {showInvoiceModal && selectedInvoice && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4 overflow-y-auto">
          <div className="bg-white rounded-xl w-full max-w-2xl my-8">
            {/* Modal Header */}
            <div className="flex items-center justify-between px-6 py-4 border-b">
              <h2 className="text-xl font-bold">Invoice Details</h2>
              <div className="flex items-center gap-2">
                <button
                  onClick={handlePrintInvoice}
                  className="flex items-center gap-2 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700"
                  data-testid="print-invoice-btn"
                >
                  <Printer className="h-4 w-4" />
                  Print
                </button>
                <button
                  onClick={() => setShowInvoiceModal(false)}
                  className="p-2 hover:bg-gray-100 rounded-lg"
                >
                  <X className="h-5 w-5" />
                </button>
              </div>
            </div>

            {/* Invoice Content (Printable) */}
            <div ref={invoicePrintRef} className="p-6">
              {/* Invoice Header */}
              <div className="invoice-header text-center mb-8 pb-6 border-b-2 border-gray-300">
                <h1 className="text-3xl font-bold text-blue-600 mb-2">INVOICE</h1>
                <p className="text-gray-600">ERP Software Subscription</p>
              </div>

              {/* Company & Invoice Details */}
              <div className="invoice-details grid grid-cols-2 gap-8 mb-8">
                <div>
                  <h3 className="text-sm font-bold text-gray-500 uppercase mb-2">From</h3>
                  <p className="font-bold text-lg">Ncriptech Global Pvt Limited</p>
                  <p className="text-gray-600 text-sm">2nd Floor, Rainbow Apartment</p>
                  <p className="text-gray-600 text-sm">Kanaipur, Konnagar</p>
                  <p className="text-gray-600 text-sm">West Bengal - 712246</p>
                  <p className="text-gray-600 text-sm">Phone: 9836807060</p>
                  <p className="text-gray-600 text-sm">GSTIN: [Your GSTIN]</p>
                </div>
                <div className="text-right">
                  <h3 className="text-sm font-bold text-gray-500 uppercase mb-2">Invoice Details</h3>
                  <p><strong>Invoice #:</strong> {selectedInvoice.invoice_number}</p>
                  <p><strong>Date:</strong> {formatDate(selectedInvoice.created_at)}</p>
                  <p><strong>Due Date:</strong> {selectedInvoice.due_date}</p>
                  <p className="mt-2">
                    <span className={`status-badge inline-block px-3 py-1 rounded-full text-sm font-bold ${
                      selectedInvoice.status === 'paid' ? 'bg-green-100 text-green-800' : 'bg-yellow-100 text-yellow-800'
                    }`}>
                      {selectedInvoice.status?.toUpperCase()}
                    </span>
                  </p>
                </div>
              </div>

              {/* Bill To */}
              <div className="mb-8 p-4 bg-gray-50 rounded-lg">
                <h3 className="text-sm font-bold text-gray-500 uppercase mb-2">Bill To</h3>
                <p className="font-bold text-lg">{subscription?.tenant_name}</p>
                <p className="text-gray-600">{subscription?.subscription?.plan_type} Plan</p>
              </div>

              {/* Invoice Table */}
              <table className="invoice-table w-full border-collapse mb-6">
                <thead>
                  <tr className="bg-gray-100">
                    <th className="border border-gray-300 p-3 text-left">Description</th>
                    <th className="border border-gray-300 p-3 text-left">Period</th>
                    <th className="border border-gray-300 p-3 text-right">Amount</th>
                  </tr>
                </thead>
                <tbody>
                  <tr>
                    <td className="border border-gray-300 p-3">
                      ERP Software Subscription ({selectedInvoice.plan_type || subscription?.subscription?.plan_type})
                    </td>
                    <td className="border border-gray-300 p-3">
                      {selectedInvoice.billing_period_start} to {selectedInvoice.billing_period_end}
                    </td>
                    <td className="border border-gray-300 p-3 text-right">
                      ₹{selectedInvoice.base_amount ? selectedInvoice.base_amount?.toLocaleString('en-IN') : selectedInvoice.amount?.toLocaleString('en-IN')}
                    </td>
                  </tr>
                  {selectedInvoice.gst_amount > 0 && (
                    <tr>
                      <td colSpan="2" className="border border-gray-300 p-3 text-right text-gray-600">
                        GST ({selectedInvoice.gst_percent || 18}%)
                      </td>
                      <td className="border border-gray-300 p-3 text-right text-gray-600">
                        ₹{selectedInvoice.gst_amount?.toLocaleString('en-IN')}
                      </td>
                    </tr>
                  )}
                  <tr className="total-row bg-blue-50">
                    <td colSpan="2" className="border border-gray-300 p-3 text-right font-bold">
                      Total Amount
                    </td>
                    <td className="border border-gray-300 p-3 text-right font-bold text-lg">
                      ₹{selectedInvoice.amount?.toLocaleString('en-IN')}
                    </td>
                  </tr>
                </tbody>
              </table>

              {/* Amount in Words */}
              <div className="bg-gray-50 p-4 rounded-lg mb-6">
                <p className="text-sm text-gray-600">
                  <strong>Amount in Words:</strong> {numberToWords(selectedInvoice.amount || 0)} Rupees Only
                </p>
              </div>

              {/* Payment Info (if paid) */}
              {selectedInvoice.status === 'paid' && (
                <div className="bg-green-50 border border-green-200 p-4 rounded-lg mb-6">
                  <h3 className="font-bold text-green-800 mb-2">Payment Information</h3>
                  <div className="grid grid-cols-2 gap-4 text-sm">
                    <p><strong>Payment Date:</strong> {formatDate(selectedInvoice.payment_date)}</p>
                    <p><strong>Payment Method:</strong> {selectedInvoice.payment_method?.replace('_', ' ').toUpperCase()}</p>
                    {selectedInvoice.transaction_id && (
                      <p><strong>Transaction ID:</strong> {selectedInvoice.transaction_id}</p>
                    )}
                  </div>
                </div>
              )}

              {/* Footer */}
              <div className="footer text-center mt-8 pt-4 border-t border-gray-200 text-gray-500 text-sm">
                <p className="font-medium">Thank you for your business!</p>
                <p className="mt-2">Ncriptech Global Pvt Limited</p>
                <p>2nd Floor, Rainbow Apartment, Kanaipur, Konnagar, West Bengal - 712246</p>
                <p>Phone: 9836807060 | Email: support@cloudcampus.site</p>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default MySubscription;
